import { ReactNode } from 'react';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import Icon, { IconType } from 'components/Icon';
import Moon from 'components/Widget/Moon';
import { FrameMoonProgress } from 'hooks/useFrameMoonProgress';
import { unthemed } from 'tokens';
import * as S from './styles';

interface Props {
	frame: Frame;
	isImageLoading: boolean;
	// Callers hold useFrameMoonProgress themselves — the indicator unmounts in the gap between a
	// frame turning READY and its <img> mounting, and a hook in here would reset progress to 0.
	moon: FrameMoonProgress;
	// Callers own the sizing: the canvas passes scene coords, the frame inspector passes
	// screen px divided by its viewport scale. Both end up the same size to the eye.
	moonSize: number;
	fontSize: number;
	gap: number;
	// Rendered under the caption — the inspector hangs its "Revert" button here on error.
	children?: ReactNode;
}

const STATUS_MAX_CHARS = 60;

const truncate = (s: string, max: number) => (s.length > max ? s.slice(0, max).trimEnd() + '…' : s);

//------------------------------------------------------------------------------------------------
// FRAME LOADING INDICATOR
//------------------------------------------------------------------------------------------------

// The one loading indicator for a frame — moon phase driven by real generation progress, plus a
// shimmering caption of the current status. Every surface that shows a frame renders this so the
// styles can't drift apart.
const FrameLoadingIndicator = ({
	frame,
	isImageLoading,
	moon,
	moonSize,
	fontSize,
	gap,
	children,
}: Props) => {
	const isProcessing = frame.metadata.status === FrameStatus.PROCESSING;
	const isError = frame.metadata.status === FrameStatus.ERROR;

	const caption =
		isProcessing || isError
			? truncate(frame.metadata.statusMessage ?? '', STATUS_MAX_CHARS)
			: isImageLoading
				? 'Loading frame…'
				: '';

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderCaption = () => {
		if (!caption) return null;
		// Error is terminal — no progress is being made, so the caption doesn't shimmer.
		const Text = isError ? S.Caption : S.ShimmerCaption;
		return (
			<Text data-testid="frame-loading-caption" fontSize={fontSize} marginTop={gap}>
				{caption}
			</Text>
		);
	};

	return (
		<S.Base data-testid="frame-loading-indicator">
			{isError ? (
				<Icon type={IconType.ERROR} width={moonSize} height={moonSize} color={unthemed.gray[100]} />
			) : (
				<S.MoonWrapper data-testid="frame-loading-moon">
					<Moon progress={moon.progress} size={moonSize} isBreathing={moon.isIndeterminate} />
				</S.MoonWrapper>
			)}
			{renderCaption()}
			{children}
		</S.Base>
	);
};

export default FrameLoadingIndicator;
